import { makeAssistantToolUI } from '@assistant-ui/react'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import type {
  ToolActionHandlers,
  ToolActionKind,
} from '@/components/agent/tools/_shared/chat-tool-actions'
import { ToolCardActions } from '@/components/agent/tools/_shared/ToolCardActions'
import { GetNodeNeighborhoodView } from '@/components/agent/tools/GetNodeNeighborhoodView'
import { api } from '@/services/api'
import { getOrCreateInvestigationsStore } from '@/stores/investigations-store'
import { emptyViewState, type GraphView } from '@/types/graph-view'
import { getHydratedNodeLabel } from '@/utils/entity-display'
import {
  extractEntityIds,
  type GetNodeNeighborhoodArgs,
  type GetNodeNeighborhoodResult,
  parseResult,
} from './get-node-neighborhood'
import { useApplyContributionToGraph } from './useApplyContributionToGraph'
import { useToolCardActions } from './useToolCardActions'

type Status = { type: 'running' | 'complete' | 'incomplete' | string }

interface GetNodeNeighborhoodRenderProps {
  args: GetNodeNeighborhoodArgs
  result?: GetNodeNeighborhoodResult | string
  status: Status
}

/**
 * Connected `get_node_neighborhood` tool card: parses the backend payload,
 * hydrates the referenced neighbourhood into a GraphView for the canvas
 * actions, resolves the action footer from the chat context, and hands pure
 * data to GetNodeNeighborhoodView.
 */
function GetNodeNeighborhoodRender({ args, result, status }: GetNodeNeighborhoodRenderProps) {
  const navigate = useNavigate()
  const { caseId } = useParams<{ caseId: string }>()
  const applyToGraph = useApplyContributionToGraph(caseId)
  const [pending, setPending] = useState<ToolActionKind | null>(null)

  const running = status.type === 'running'
  const parsed = useMemo(() => parseResult(result), [result])
  const errored = parsed?.status === 'error' || status.type === 'incomplete'
  const entityIds = useMemo(() => extractEntityIds(parsed), [parsed])
  const centerName = parsed?.center?.name ?? 'node'

  const handlers = useMemo<ToolActionHandlers>(() => {
    if (parsed?.status !== 'ok' || !parsed.hydrate || !caseId) return {}
    const title = parsed.hydrate.label ?? `Around ${centerName}`

    const hydrateGraphView = async (): Promise<GraphView | null> => {
      if (!parsed.hydrate || !caseId) return null
      try {
        const data = await api.graph.hydrateGraph(caseId, {
          node_ids: parsed.hydrate.node_ids,
          relationship_ids: parsed.hydrate.relationship_ids,
        })
        return {
          meta: {
            source: 'assistant',
            createdAt: new Date().toISOString(),
            caseId,
            label: title,
          },
          nodes: data.nodes.map(n => ({
            id: n.id,
            labels: n.labels,
            typeLabel: n.labels[0],
            // Walk the documented display-name fallback chain (incl. the
            // schema-stamped `_display_name`) so canvas chips don't fall back
            // to "Default" when the hydrate response leaves `n.name` empty.
            name: getHydratedNodeLabel(n),
            properties: n.properties ?? {},
          })),
          edges: data.relationships.map(r => ({
            id: r.id,
            type: r.type,
            source: r.source,
            target: r.target,
            properties: r.properties ?? {},
          })),
          view: emptyViewState(),
        }
      } catch (e) {
        console.error('[Neighborhood] hydrate failed:', e)
        toast.error('Failed to load neighbourhood on graph')
        return null
      }
    }

    // Hydration is a network round-trip; flag the clicked action so its button
    // shows a spinner (and the footer locks) until we navigate away or fail.
    const run = async (kind: ToolActionKind, mode: 'explore' | 'add') => {
      setPending(kind)
      try {
        const view = await hydrateGraphView()
        if (!view) return
        applyToGraph(mode, view, title)
      } finally {
        setPending(null)
      }
    }
    return {
      showOnGraph: () => void run('showOnGraph', 'explore'),
      showSeparately: () => void run('showSeparately', 'explore'),
      addToGraph: () => void run('addToGraph', 'add'),
      saveAsInvestigation: async () => {
        const view = await hydrateGraphView()
        if (!view) return
        const store = getOrCreateInvestigationsStore({ caseId })
        store
          .getState()
          .setTransient(view, title, `neighborhood-${parsed.center?.element_id ?? 'unknown'}`)
        await store.getState().saveTransient(title)
        navigate(`/cases/${caseId}/graph`)
      },
    }
  }, [caseId, parsed, centerName, navigate, applyToGraph])

  const actionsModel = useToolCardActions(entityIds, handlers)
  const actions = actionsModel ? (
    <ToolCardActions model={actionsModel} handlers={handlers} pending={pending} />
  ) : null

  return (
    <GetNodeNeighborhoodView
      args={args}
      parsed={parsed}
      running={running}
      errored={errored}
      actions={actions}
    />
  )
}

export const GetNodeNeighborhoodToolUI = makeAssistantToolUI<
  GetNodeNeighborhoodArgs,
  GetNodeNeighborhoodResult | string
>({
  toolName: 'get_node_neighborhood',
  render: GetNodeNeighborhoodRender,
})
